import type { FB } from "@binale-tech/shared";
import { Category } from "@domain/models/Category";
import { Creditor, Debitor } from "@domain/models/Creditor";
import type { IGenericItem, IGenericRecord } from "@domain/models/Interfaces";
import { Payment } from "@domain/models/Payment";
import { Tag } from "@domain/models/Tag";

type ReadFate = "read" | "unread";

// Every RTDB key and whether a reader uses it; a key added to FB.Record fails tsc until classified.
export const RECORD_READS = {
    key: "read",
    date: "read",
    year: "read",
    period: "read",
    day: "read",
    updatedBy: "read",
    items: "read",
    groupId: "unread",
    priority: "unread",
    // Required: kbInit reads the KB order through snap.priority; the backend must keep writing it.
    ".priority": "read",
    category: "read",
    creditor: "read",
    debetor: "read",
    categoryNum: "unread",
    creditorNum: "unread",
    debitorNum: "unread",
    draft: "read",
    journaled: "read",
    avis: "read",
    lastschrift: "read",
    num: "read",
    color: "read",
    notesColor: "read",
    review: "read",
    currency: "read",
    originalAmount: "read",
    falligkeit: "read",
    documents: "read",
    cancellation: "read",
    contactId: "read",
    saldoSourceId: "read",
    countryCode: "read",
    vatNumber: "read",
    bankingTransactionId: "read",
    cashRegisterTransactionId: "read",
    aiReview: "read",
    recordOrigin: "read",
    ustvaExportIds: "read",
} as const satisfies Record<keyof FB.Record, ReadFate>;

export const RECORD_ITEM_READS = {
    brutto: "read",
    bu: "read",
    belegfeld1: "read",
    belegfeld2: "read",
    text: "read",
    text2: "read",
    extra: "read",
    originalAmount: "read",
    USt13b: "read",
    category: "read",
    creditor: "read",
    debetor: "read",
    tag: "read",
    categoryNum: "unread",
    creditorNum: "unread",
    debitorNum: "unread",
} as const satisfies Record<keyof FB.RecordItem, ReadFate>;

export const PAYMENT_READS = {
    _date: "read",
    amount: "read",
    discount: "read",
    _type: "read",
    keySrc: "read",
    keyRepr: "read",
    autoFEBalancingAZRecordKey: "read",
    autoFEDiscountRecordKey: "read",
    paymentSources: "read",
} as const satisfies Record<keyof FB.Payment, ReadFate>;

/** Reads one RTDB record item into the input of GenericItem. */
export function readFirebaseRecordItem(value: FB.RecordItem): IGenericItem {
    const { creditor, category, debetor, tag, USt13b, ...rest } = value;
    return {
        ...rest,
        creditor: creditor ? Creditor.unserialize({ ...creditor }) : undefined,
        category: category ? Category.unserialize({ ...category }) : undefined,
        debetor: debetor ? Debitor.unserialize({ ...debetor }) : undefined,
        tag: tag ? Tag.unserialize({ ...tag }) : (tag as null | undefined),
        USt13b: USt13b ?? undefined,
    };
}

/** Reads an RTDB record node into the input of the record constructors; unread keys pass through as today. */
export function readFirebaseRecord(value: FB.Record): IGenericRecord {
    const { date, falligkeit, creditor, category, debetor, items, notesColor, originalAmount, documents, ...rest } =
        value;
    return {
        ...rest,
        date: new Date(date),
        falligkeit: falligkeit ? new Date(falligkeit) : undefined,
        creditor: creditor ? Creditor.unserialize({ ...creditor }) : undefined,
        category: category ? Category.unserialize({ ...category }) : undefined,
        debetor: debetor ? Debitor.unserialize({ ...debetor }) : undefined,
        items: items?.map(readFirebaseRecordItem),
        notesColor: notesColor ?? undefined,
        originalAmount: originalAmount ?? undefined,
        documents,
    };
}

/** Reads an RTDB payment node; the node path is the payment key. */
export function readFirebasePayment(key: string, value: FB.Payment): Payment {
    return Payment.unserialize({ ...value, key });
}
